const REDACTED = '[REDACTED]';
const AUTH_LINK = /\/(?:reset-password|accept-invite)\/([^\s/?#"'<>\\]+)/g;

// Decode only for inspection. Unrelated strings retain their original encoding.
// Decoding each valid run also tolerates malformed escapes elsewhere in a URL.
function decoded(value: string): string {
  let current = value;
  while (true) {
    const next = current.replace(/(?:%[0-9a-f]{2})+/gi, (run) => {
      try { return decodeURIComponent(run); } catch { return run.replace(/%([0-7][0-9a-f])/gi, (_escape, hex: string) => String.fromCharCode(parseInt(hex, 16))); }
    });
    if (next === current) return current;
    current = next;
  }
}

function traversable(value: unknown): value is object {
  if (!value || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  return Array.isArray(value) || value instanceof Error || prototype === Object.prototype || prototype === null;
}

/** Remove authentication-link credentials and their copies without mutating SDK inputs. */
export function redactAuthLinks<T>(value: T, source?: unknown): T {
  const secrets = new Set<string>();
  const visited = new WeakSet<object>();
  function collect(input: unknown): void {
    if (typeof input === 'string') {
      for (const match of decoded(input).matchAll(AUTH_LINK)) {
        if (match[1] !== REDACTED && match[1] !== '[token]') secrets.add(match[1]);
      }
    } else if (traversable(input) && !visited.has(input)) {
      visited.add(input);
      for (const [key, descriptor] of Object.entries(Object.getOwnPropertyDescriptors(input))) {
        collect(key);
        if ('value' in descriptor) collect(descriptor.value);
      }
    }
  }
  collect(value);
  collect(source);
  if (!secrets.size) return value;

  // Longer values first so a credential that prefixes another cannot leave a suffix.
  const ordered = [...secrets].sort((a, b) => b.length - a.length);
  function scrubString(input: string): string {
    const inspected = decoded(input);
    let result = inspected;
    for (const secret of ordered) result = result.split(secret).join(REDACTED);
    return result === inspected ? input : result;
  }
  const copies = new WeakMap<object, object>();
  function scrub(input: unknown): unknown {
    if (typeof input === 'string') return scrubString(input);
    if (!traversable(input)) return input;
    const existing = copies.get(input);
    if (existing) return existing;
    const copy = Array.isArray(input) ? [] : Object.create(Object.getPrototypeOf(input));
    copies.set(input, copy);
    for (const [key, descriptor] of Object.entries(Object.getOwnPropertyDescriptors(input))) {
      if ('value' in descriptor) descriptor.value = scrub(descriptor.value);
      Object.defineProperty(copy, scrubString(key), descriptor);
    }
    return copy;
  }
  return scrub(value) as T;
}
